import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Smartphone, Package, Globe2, Boxes, LayoutDashboard, ShoppingCart, ArrowRight,
} from "lucide-react";
import PageHero from "../components/PageHero";
import CTABanner from "../components/CTABanner";
import { SectionLabel } from "../components/Shared";
import { IMAGES, STATS } from "../config/siteConfig";
import { stagger, fadeUp, viewportOnce } from "../lib/motion";

const PRODUCTS = [
  {
    icon: Smartphone,
    title: "Mobile Apps (iOS & Android)",
    desc: "Custom mobile applications for iPhone and Android — from 15–25 screens with CMS, SSL, social integration and annual maintenance.",
  },
  {
    icon: Package,
    title: "Product Development (Mobile | Web)",
    desc: "End-to-end product builds from concept to launch — scalable, secure and UX-focused platforms delivered with agile sprints.",
  },
  {
    icon: Globe2,
    title: "Managed Website Products",
    desc: "Fully dynamic, mobile-friendly websites and CMS products — 15–25 pages, self-configurable, training videos and go-live support.",
  },
  {
    icon: Boxes,
    title: "ERP & Inventory Systems",
    desc: "Tailor-made ERP covering inventory, engineering, production planning, attendance and reporting for operations teams.",
  },
  {
    icon: LayoutDashboard,
    title: "CRM & Business Platforms",
    desc: "Custom CRM, admin panels and business portals that help teams manage customers, workflows and growth.",
  },
  {
    icon: ShoppingCart,
    title: "Marketplace & E-commerce",
    desc: "Hyperlocal marketplaces, deals platforms and commerce experiences for buyers and sellers across web and mobile.",
  },
];

export default function ProductsPage() {
  const productsStat = STATS.find((s) => s.label === "Products");

  return (
    <>
      <PageHero
        label="Products"
        title="Software products built"
        highlight="to go live."
        subtitle="From mobile apps and managed websites to ERP, CRM and marketplaces — INRISOFT delivers products that ship, scale and stay supported."
        image={IMAGES.employers}
      />

      <section className="bg-[#111111] py-16 md:py-20" data-testid="products-stats">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col md:flex-row md:items-end justify-between gap-8">
          <div>
            <SectionLabel dark>Product Portfolio</SectionLabel>
            <h2 className="font-display text-4xl md:text-5xl text-white tracking-tight">
              {productsStat ? `${productsStat.value}${productsStat.suffix}` : "20+"} Products Delivered
            </h2>
          </div>
          <p className="font-body text-white/60 max-w-md leading-relaxed">
            Projects | Development | Software Products | Mobile Apps / ERP | CRM | Inventory Management | Custom Software and Collabs.
          </p>
        </div>
      </section>

      <section className="bg-[#F7F7F5] py-24 md:py-36" data-testid="products-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl mb-14">
            <SectionLabel>What We Build</SectionLabel>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight text-[#111111]">
              Product lines that match how <span className="text-[#C62828]">businesses grow.</span>
            </h2>
          </div>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={viewportOnce}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5"
          >
            {PRODUCTS.map((p) => (
              <motion.article
                key={p.title}
                variants={fadeUp}
                data-testid={`product-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="bg-white border border-black/10 rounded-sm p-8 hover:border-[#C62828] transition-colors duration-400"
              >
                <p.icon size={26} strokeWidth={1.5} className="text-[#C62828] mb-5" />
                <h3 className="font-body font-600 text-xl text-[#111111] mb-3">{p.title}</h3>
                <p className="font-body text-[#555] text-sm leading-relaxed">{p.desc}</p>
              </motion.article>
            ))}
          </motion.div>

          <Link
            to="/services"
            data-testid="products-services-cta"
            className="group inline-flex items-center gap-2 mt-12 font-accent uppercase tracking-widest text-sm text-[#111111] hover:text-[#C62828] transition-colors"
          >
            Explore Our Services
            <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </section>

      <CTABanner heading="Have a product idea?" highlight="Let's build it." />
    </>
  );
}
